import { useState, type FormEvent, type ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';
import { Card } from '../card/card.tsx';
import { Field, type FieldControlProps } from '../field/field.tsx';
import { Input } from '../input/input.tsx';
import { Select, type SelectOption } from '../select/select.tsx';
import { JourneyEmployeeFields } from './journey-employee-fields.tsx';
import { JourneyFormActions } from './journey-form-actions.tsx';
import {
  EMPTY_JOURNEY_DRAFT,
  toJourneySubmitValue,
  validateJourneyDraft,
  type JourneyDraft,
  type JourneyFormErrors,
} from './journey-draft.ts';
import type { JourneyFormLabels } from './journey-form-labels.ts';
import type { JourneyLimits, JourneySubmitValue } from './types.ts';
import { useFieldErrorFocus } from './use-field-error-focus.ts';
import { useStandingErrors } from './use-standing-errors.ts';

export type { JourneyFormLabels };

/** The field wiring for the injected manager picker, plus the same state as a flag. */
export interface JourneyManagerControl extends FieldControlProps {
  invalid: boolean;
}

export interface JourneyFormProps {
  typeOptions: readonly SelectOption[];
  /** The chosen manager. The picker is the caller's, so its value is too. */
  managerId: string | null;
  /**
   * The manager picker. Put `id` on the element that takes focus: the label
   * points at it, and an invalid form puts the caret there.
   */
  renderManager: (control: JourneyManagerControl) => ReactNode;
  /** The link shown under the employee field when that pair already has a running journey. */
  renderRunningJourneyLink?: (journeyId: string) => ReactNode;
  /** Only called with a value that passed the form's own checks. */
  onSubmit: (value: JourneySubmitValue) => void;
  onCancel?: () => void;
  submitting?: boolean;
  /** What the server refused. A message goes away once its field is edited. */
  errors?: JourneyFormErrors;
  /** A failure that belongs to no field. */
  formError?: string;
  /** The API's bounds, checked before anything is sent. */
  limits: JourneyLimits;
  labels: JourneyFormLabels;
  className?: string;
}

const NO_ERRORS: JourneyFormErrors = {};

/**
 * A new journey: which type, which manager, which employee, from which day.
 * One running journey per employee per manager is the server's to judge; its
 * refusal comes back through `errors` and lands on the employee field.
 */
export function JourneyForm({
  typeOptions,
  managerId,
  renderManager,
  renderRunningJourneyLink,
  onSubmit,
  onCancel,
  submitting = false,
  errors: serverErrors = NO_ERRORS,
  formError,
  limits,
  labels,
  className,
}: JourneyFormProps) {
  const { formRef, focusFirstError } = useFieldErrorFocus(serverErrors);
  const [draft, setDraft] = useState<JourneyDraft>(EMPTY_JOURNEY_DRAFT);
  const [attempted, setAttempted] = useState(false);
  const { standing, dismiss } = useStandingErrors<keyof JourneyFormErrors>(serverErrors);

  /* The refusal was about this pair; another manager is another pair. */
  const [seenManager, setSeenManager] = useState(managerId);
  if (seenManager !== managerId) {
    setSeenManager(managerId);
    dismiss('manager_user_id');
    dismiss('running_journey_id');
  }

  const errors: JourneyFormErrors = {
    ...standing,
    ...(attempted
      ? validateJourneyDraft(draft, managerId, labels.errors, limits)
      : NO_ERRORS),
  };

  function setField(field: keyof JourneyDraft, value: string) {
    setDraft((current) => ({ ...current, [field]: value }));
    dismiss(field);
    if (field === 'employee_email') dismiss('running_journey_id');
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setAttempted(true);

    const problems = validateJourneyDraft(draft, managerId, labels.errors, limits);
    if (Object.keys(problems).length > 0 || managerId === null) {
      focusFirstError();
      return;
    }
    onSubmit(toJourneySubmitValue(draft, managerId));
  }

  return (
    <form
      ref={formRef}
      data-slot='journey-form'
      noValidate
      onSubmit={submit}
      className={cn('flex flex-col gap-3.5', className)}
    >
      <Card className='flex flex-col gap-4'>
        <Field label={labels.type} error={errors.journey_type_id} required>
          {(control) => (
            <Select
              {...control}
              name='journey_type_id'
              wrapperClassName='w-full'
              className='w-full'
              options={[{ value: '', label: labels.typePlaceholder }, ...typeOptions]}
              value={draft.journey_type_id}
              onChange={(event) => setField('journey_type_id', event.target.value)}
            />
          )}
        </Field>

        <Field
          label={labels.manager}
          description={labels.managerDescription}
          error={errors.manager_user_id}
          required
        >
          {(control) =>
            renderManager({ ...control, invalid: control['aria-invalid'] === true })
          }
        </Field>

        <JourneyEmployeeFields
          draft={draft}
          errors={errors}
          labels={labels}
          renderRunningJourneyLink={renderRunningJourneyLink}
          onChange={setField}
        />

        <Field
          label={labels.startDate}
          description={labels.startDateDescription}
          error={errors.start_date}
          required
        >
          {(control) => (
            <Input
              {...control}
              name='start_date'
              type='date'
              className='mobile:w-auto'
              value={draft.start_date}
              onChange={(event) => setField('start_date', event.target.value)}
            />
          )}
        </Field>
      </Card>

      <JourneyFormActions
        formError={formError}
        onCancel={onCancel}
        submitting={submitting}
        labels={labels}
      />
    </form>
  );
}
